import {Box, Button, Card, CircularProgress, Stack, Typography} from "@mui/joy"
import {useSessionCookie} from "../hooks/useSessionCookie";
import {appRoute} from "../utils/routes";
import React, {useEffect, useRef} from "react"
import {CheckCircle2, MailWarning} from "lucide-react"
import {Link, useSearchParams} from "react-router-dom"
import {motion} from "motion/react"
import {useTranslation} from "react-i18next"
import {useUITheme} from "../hooks/useUITheme"
import {useAsync} from "../hooks/useAsyncResult"
import {verifyEmail as _verifyEmail} from "../services/api/user"

/**
 * Landing page for the link in the verification email.
 *
 * The token in the URL is the credential, so the page is public — the address
 * is frequently opened in a different browser (or on a phone) from the one the
 * user is signed in on.
 */
const VerifyEmail = () => {
    const uiTheme = useUITheme()
    const {t} = useTranslation()
    const [searchParams] = useSearchParams()
    const {userSession, setUserSession} = useSessionCookie()
    const token = searchParams.get("token")

    // Guards against React 18 StrictMode double-invoking the effect, which
    // would spend the single-use token on the first call and report the second
    // as already consumed.
    const submitted = useRef(false)
    const {execute, loading, isSuccess, error} = useAsync(_verifyEmail, false, false, !!token)

    useEffect(() => {
        if (!token || submitted.current)
            return
        submitted.current = true
        execute(token)
    }, [token, execute])

    /**
     * Refresh the cached session so the unverified banner disappears without
     * requiring a sign-out — only when this browser is the signed-in one.
     */
    useEffect(() => {
        if (!isSuccess || !userSession?.currentUser)
            return

        setUserSession({
            ...userSession,
            currentUser: {
                ...userSession.currentUser,
                emailVerified: true
            }
        }, new Date(userSession.expiresAt))
    }, [isSuccess])

    const renderShell = (children: React.ReactNode) =>
        <Stack sx={{overflowX: "hidden"}}>
            <Stack paddingX={{xs: 2, sm: 3}} justifyContent={"center"} gap={2} alignItems={"center"} paddingY={3}
                   sx={{backgroundColor: "background.surface"}}>
                {/* The wrapper needs its own width: without it the card sizes to its content and
                    overflows narrow screens. */}
                <motion.div
                    style={{width: "100%", maxWidth: "35rem"}}
                    initial={{opacity: 0, y: 16}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.4}}>
                    <Card variant={"outlined"}
                          sx={{
                              paddingX: {xs: 2.5, sm: 5},
                              alignItems: "center",
                              gap: 2,
                              width: "100%",
                              boxSizing: "border-box",
                              paddingY: 4
                          }}>
                        <Box component={"img"}
                             width={"10rem"}
                             height={"auto"}
                             src={uiTheme.fullLogo}
                             alt={'LinguaScore Logo'}/>
                        {children}
                    </Card>
                </motion.div>
            </Stack>
        </Stack>

    if (!token)
        return renderShell(
            <Stack gap={2} alignItems="center" width="100%">
                <MailWarning style={{fontSize:50}} color="var(--joy-palette-danger-500)"/>
                <Typography level="h3" textAlign="center">{t("LINK_INVALID")}</Typography>
                <Link to={appRoute()} style={{textDecoration: "none", width: "100%"}}>
                    <Button size="lg" fullWidth>{t("goToDashboard")}</Button>
                </Link>
            </Stack>)

    if (loading)
        return renderShell(
            <Stack gap={2} alignItems="center" width="100%">
                <CircularProgress size="lg"/>
                <Typography level="body-md" textColor="neutral.600" textAlign="center">
                    {t("verifyEmailLoadingMessage")}
                </Typography>
            </Stack>)

    if (isSuccess)
        return renderShell(
            <Stack gap={2} alignItems="center" width="100%">
                <CheckCircle2 size={48} color="var(--joy-palette-success-500)"/>
                <Typography level="h3" textAlign="center">{t("verifyEmailSuccessTitle")}</Typography>
                <Typography level="body-md" textColor="neutral.600" textAlign="center">
                    {t("verifyEmailSuccessDescription")}
                </Typography>
                <Link to={appRoute()} style={{textDecoration: "none", width: "100%"}}>
                    <Button size="lg" fullWidth>{t("goToDashboard")}</Button>
                </Link>
            </Stack>)

    return renderShell(
        <Stack gap={2} alignItems="center" width="100%">
            <MailWarning size={48} color="var(--joy-palette-danger-500)"/>
            <Typography level="h3" textAlign="center">{t("verifyEmailFailedTitle")}</Typography>
            <Typography level="body-md" textColor="neutral.600" textAlign="center">
                {error || t("LINK_INVALID")}
            </Typography>
            <Link to={appRoute()} style={{textDecoration: "none", width: "100%"}}>
                <Button variant="outlined" size="lg" fullWidth>{t("goToDashboard")}</Button>
            </Link>
        </Stack>)
}

export default VerifyEmail
